'use client'

import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Avatar } from '../../components/Avatar'
import { Badge } from '../../components/Badge'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from '../../components/Dropdown'
import { Navbar, NavbarSection, NavbarSpacer } from '../../components/Navbar'
import {
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarHeader,
    SidebarHeading,
    SidebarItem,
    SidebarLabel,
    SidebarSection,
    SidebarSpacer,
} from '../../components/Sidebar'
import { SidebarLayout } from '../../components/SidebarLayout'
import {
    ArrowRightStartOnRectangleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    InformationCircleIcon,
    LightBulbIcon,
    PlusIcon,
    ShieldCheckIcon,
    UserCircleIcon,
    LanguageIcon,
    ChevronRightIcon,
    CheckIcon,
    BellIcon,
    QueueListIcon,
    ClipboardDocumentCheckIcon,
    TruckIcon,
    CheckBadgeIcon,
    ShoppingBagIcon,
    PlusCircleIcon,
    MinusCircleIcon,
    PauseCircleIcon,
    CogIcon,
    BuildingStorefrontIcon,
    RocketLaunchIcon,
    Squares2X2Icon,
    ShoppingCartIcon,
    ExclamationTriangleIcon,
    GlobeAltIcon,
    TableCellsIcon,
    RectangleGroupIcon,
    Square3Stack3DIcon,
    DevicePhoneMobileIcon,
    UsersIcon,
    InboxIcon,
    ChatBubbleOvalLeftEllipsisIcon,
    DocumentIcon,
    DocumentPlusIcon,
    DocumentCheckIcon,
    MapPinIcon,
    DocumentTextIcon,
    CheckCircleIcon,
    ArrowsUpDownIcon,
    SignalIcon,
    ArrowsRightLeftIcon,
    XMarkIcon,
    ClockIcon,
} from '@heroicons/react/16/solid'
import {
    Cog6ToothIcon,
    HomeIcon,
    QuestionMarkCircleIcon,
    SparklesIcon,
    Square2StackIcon,
    TicketIcon,
    UserGroupIcon,
    RectangleStackIcon,
} from '@heroicons/react/20/solid'
import { useContext, useMemo, useState } from 'react'
import { UserContext } from '../../context/userContext'
import { useNotifications } from '../../hooks/useNotifications'
import { Divider } from '../../components/Divider'
import { ThemeToggle } from '../../components/ThemeToggle'
import showLoadingToast from '../../components/Toasts/ShowLoadingToast'
import { SuperAdminProvider } from '../../provider/superadminProvider'
import { useTranslation } from 'react-i18next';
import { useVersionCheck } from '../../hooks/useVersionCheck'
import { TableCell } from '../../components/Table'
import { cn } from '../../lib/utils'
import {
    DEFAULT_NOTIFICATION_ICON,
    findNotificationCategory,
    formatNotificationRelativeTime,
    groupNotificationsByTimeline,
} from '../../utils/notificationDisplay'

const BREADCRUMB_LABELS: Record<string, string> = {
    dashboard: 'Dashboard',
    notifications: 'Notifications',
    users: 'Users',
    auditlog: 'Audit Log',
    warehouses: 'Warehouses',
    tracking: 'Tracking',
    pickups: 'Pickup',
    standalone: 'Standalone',
    completed: 'Completed',
    stocks: 'Stocks',
    stockin: 'Stock In',
    stockout: 'Stock Out',
    stockadjust: 'Stock Adjust',
    stockbalancetransfer: 'Stock Balance Transfer',
    salesorders: 'Sales Orders',
    inventories: 'Inventories',
    products: 'Products',
    groups: 'Groups',
    categories: 'Categories',
    departments: 'Departments',
    setup: 'Setup',
    suppliers: 'Suppliers',
    customers: 'Customers',
    logistics: 'Logistics',
    reports: 'Reports',
    stockbalance: 'Stock Balance',
    stockmovement: 'Stock Movement',
    stockbybatch: 'Stock By Batch',
    app: 'Mobile App',
    announcements: 'Announcements',
    precallplans: 'Pre Call Plans',
    postcallplans: 'Post Call Plans',
    locations: 'Locations',
    checkins: 'Check Ins',
    usercurrentlocation: 'User Tracking',
    updates: 'App Updates',
    'ai-report-helper': 'AI Report Helper',
    myaccount: 'My Account',
}

function titleCase(segment: string) {
    return segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Derives a breadcrumb label from the route path — maps known segments to friendly names, falls back to title-casing the last segment. */
function usePageBreadcrumb(pathname: string) {
    const segments = pathname.split('/').filter((s) => s && s !== 'gp' && !/^(add|edit|view|\d+|[0-9a-f-]{8,})$/i.test(s))
    const last = segments[segments.length - 1]
    if (!last) return 'Dashboard'
    return BREADCRUMB_LABELS[last] ?? titleCase(last)
}

function AccountDropdownMenu({ anchor }: { anchor: 'top start' | 'bottom end' }) {
    const navigate = useNavigate();
    const { logout, currentUser } = useContext(UserContext);

    function logoutClick(event: React.MouseEvent<HTMLElement>) {
        event.preventDefault();
        showLoadingToast("Signing out...", 100);
        setTimeout(() => {
            logout();
        }, 1000)
    }

    return (
        <DropdownMenu className="min-w-64" anchor={anchor}>
            <DropdownItem onClick={() => navigate(`/gp/myaccount/view`)}>
                <UserCircleIcon />
                <DropdownLabel>My account</DropdownLabel>
            </DropdownItem>
            <DropdownItem onClick={() => navigate('/gp/about')}>
                <InformationCircleIcon />
                <DropdownLabel>About WMS</DropdownLabel>
            </DropdownItem>
            {/* <DropdownItem onClick={() => navigate("#")}>
                <ShieldCheckIcon />
                <DropdownLabel>Privacy policy</DropdownLabel>
            </DropdownItem>
            <DropdownItem onClick={() => navigate("#")}>
                <LightBulbIcon />
                <DropdownLabel>Share feedback</DropdownLabel>
            </DropdownItem> */}
            {/* <DropdownDivider /> */}
            <DropdownItem onClick={logoutClick}>
                <ArrowRightStartOnRectangleIcon />
                <DropdownLabel>Sign out</DropdownLabel>
            </DropdownItem>
        </DropdownMenu>
    )
}

function LanguageDropdownMenu({ anchor }: { anchor: 'top start' | 'bottom end' }) {
    const { i18n } = useTranslation();
    const [language, setLanguage] = useState(i18n.language || 'en');

    const changeLanguage = (lng: string) => {
        showLoadingToast("Changing language...", 100)
        setTimeout(() => {
            setLanguage(lng);
            i18n.changeLanguage(lng);
        }, 1000)
    };

    return (
        <DropdownMenu className="min-w-64" anchor={anchor}>
            <DropdownItem onClick={() => changeLanguage('en')} className={language === 'en' ? 'font-bold' : 'font-normal'}>
                <DropdownLabel>English</DropdownLabel>
                {language === 'en' && <CheckIcon />}
            </DropdownItem>
            <DropdownItem onClick={() => changeLanguage('cn')} className={language === 'cn' ? 'font-bold' : 'font-normal'}>
                <DropdownLabel>中文</DropdownLabel>
                {language === 'cn' && <CheckIcon />}
            </DropdownItem>
        </DropdownMenu>
    );
}

function TrackingSidebarItem() {
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => setIsExpanded(!isExpanded);

    return (
        <div className="tracking-sidebar">
            <SidebarItem onClick={toggleExpand} current={pathname.includes('gp/tracking')}>
                <ClipboardDocumentCheckIcon />
                <SidebarLabel>Tracking</SidebarLabel>
                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </SidebarItem>

            {isExpanded && (
                <div className="ml-6 py-2">
                    <SidebarItem onClick={() => navigate("/gp/tracking/pickups")} current={pathname.includes('gp/tracking/pickups')}>
                        <TruckIcon />
                        <SidebarLabel>Pickup</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/tracking/standalone")} current={pathname.includes('gp/tracking/standalone')}>
                        <QueueListIcon />
                        <SidebarLabel>Standalone</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/tracking/completed")} current={pathname.includes('gp/tracking/completed')}>
                        <CheckBadgeIcon />
                        <SidebarLabel>Completed</SidebarLabel>
                    </SidebarItem>
                </div>
            )}
        </div>
    );
}

function StockSidebarItem() {
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => setIsExpanded(!isExpanded);

    return (
        <div className="tracking-sidebar">
            <SidebarItem onClick={toggleExpand} current={pathname.includes('gp/stocks')}>
                <ShoppingBagIcon />
                <SidebarLabel>Stocks</SidebarLabel>
                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </SidebarItem>

            {isExpanded && (
                <div className="ml-6 py-2">
                    <SidebarItem onClick={() => navigate("/gp/stocks/stockin")} current={pathname.includes('gp/stocks/stockin')}>
                        <PlusCircleIcon />
                        <SidebarLabel>Stock In</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/stocks/stockout")} current={pathname.includes('gp/stocks/stockout')}>
                        <MinusCircleIcon />
                        <SidebarLabel>Stock Out</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/stocks/stockadjust")} current={pathname.includes('gp/stocks/stockadjust')}>
                        <PauseCircleIcon />
                        <SidebarLabel>Stock Adjust</SidebarLabel>
                    </SidebarItem>
                </div>
            )}
        </div>
    );
}

function InventorySidebarItem() {
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => setIsExpanded(!isExpanded);

    return (
        <div className="tracking-sidebar">
            <SidebarItem onClick={toggleExpand} current={pathname.includes('gp/inventories')}>
                <ShoppingCartIcon />
                <SidebarLabel>Inventories</SidebarLabel>
                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </SidebarItem>

            {isExpanded && (
                <div className="ml-6 py-2">
                    <SidebarItem onClick={() => navigate("/gp/inventories/products")} current={pathname.includes('gp/inventories/products')}>
                        <ShoppingBagIcon />
                        <SidebarLabel>Products</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/inventories/groups")} current={pathname.includes('gp/inventories/groups')}>
                        <RectangleGroupIcon />
                        <SidebarLabel>Groups</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/inventories/categories")} current={pathname.includes('gp/inventories/categories')}>
                        <RectangleStackIcon />
                        <SidebarLabel>Categories</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/inventories/departments")} current={pathname.includes('gp/inventories/departments')}>
                        <Square3Stack3DIcon />
                        <SidebarLabel>Departments</SidebarLabel>
                    </SidebarItem>
                </div>
            )}
        </div>
    );
}

function SetupSidebarItem() {
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => setIsExpanded(!isExpanded);

    return (
        <div className="tracking-sidebar">
            <SidebarItem onClick={toggleExpand} current={pathname.includes('gp/setup')}>
                <CogIcon />
                <SidebarLabel>Setup</SidebarLabel>
                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </SidebarItem>

            {isExpanded && (
                <div className="ml-6 py-2">
                    <SidebarItem onClick={() => navigate("/gp/setup/suppliers")} current={pathname.includes('gp/setup/suppliers')}>
                        <GlobeAltIcon />
                        <SidebarLabel>Suppliers</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/setup/customers")} current={pathname.includes('gp/setup/customers')}>
                        <UserGroupIcon />
                        <SidebarLabel>Customers</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/setup/logistics")} current={pathname.includes('gp/setup/logistics')}>
                        <TruckIcon />
                        <SidebarLabel>Logistics</SidebarLabel>
                    </SidebarItem>

                </div>
            )}
        </div>
    );
}

function ReportSidebarItem() {
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const [isExpanded, setIsExpanded] = useState(false);
    const { currentUser } = useContext(UserContext);

    const toggleExpand = () => setIsExpanded(!isExpanded);

    return (
        <div className="tracking-sidebar">
            <SidebarItem onClick={toggleExpand} current={pathname.includes('gp/report')}>
                <DocumentTextIcon />
                <SidebarLabel>Reports</SidebarLabel>
                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </SidebarItem>

            {isExpanded && (
                <div className="ml-6 py-2">
                    {(currentUser?.role.toString() !== "5") && <SidebarItem onClick={() => navigate("/gp/reports/stockbalance")} current={pathname.includes('gp/reports/stockbalance')}>
                        <TableCellsIcon />
                        <SidebarLabel>Stock Balance</SidebarLabel>
                    </SidebarItem>}
                    <SidebarItem onClick={() => navigate("/gp/reports/stockmovement")} current={pathname.includes('gp/reports/stockmovement')}>
                        <TableCellsIcon />
                        <SidebarLabel>Stock Movement</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/reports/stockbybatch")} current={pathname.includes('gp/reports/stockbybatch')}>
                        <TableCellsIcon />
                        <SidebarLabel>Stock By Batch</SidebarLabel>
                    </SidebarItem>
                </div>
            )}
        </div>
    );
}

function AppSidebarItem() {
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => setIsExpanded(!isExpanded);

    return (
        <div className="tracking-sidebar">
            <SidebarItem onClick={toggleExpand} current={pathname.includes('gp/app')}>
                <DevicePhoneMobileIcon />
                <SidebarLabel>Mobile App</SidebarLabel>
                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </SidebarItem>

            {isExpanded && (
                <div className="ml-6 py-2">
                    <SidebarItem onClick={() => navigate("/gp/app/users")} current={pathname.includes('gp/app/users')}>
                        <UsersIcon />
                        <SidebarLabel>App Users</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/app/announcements")} current={pathname.includes('gp/app/announcements')}>
                        <ChatBubbleOvalLeftEllipsisIcon />
                        <SidebarLabel>Announcements</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/app/precallplans")} current={pathname.includes('gp/app/precallplans')}>
                        <DocumentIcon />
                        <SidebarLabel>Pre Call Plans</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/app/postcallplans")} current={pathname.includes('gp/app/postcallplan')}>
                        <DocumentCheckIcon />
                        <SidebarLabel>Post Call Plans</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/app/track/locations")} current={pathname.includes('gp/app/track/locations')}>
                        <MapPinIcon />
                        <SidebarLabel>Locations</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/app/track/checkins")} current={pathname.includes('gp/app/track/checkins')}>
                        <CheckCircleIcon />
                        <SidebarLabel>Check Ins</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/app/track/usercurrentlocation")} current={pathname.includes('gp/app/track/usercurrentlocation')}>
                        <SignalIcon />
                        <SidebarLabel>User Tracking</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => navigate("/gp/app/updates")} current={pathname.includes('gp/app/updates')}>
                        <DocumentPlusIcon />
                        <SidebarLabel>App Updates</SidebarLabel>
                    </SidebarItem>

                </div>
            )}
        </div>
    );
}

function formatSessionDuration(totalMinutes: number): string {
    if (totalMinutes <= 0) return 'Expired'
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    if (hours === 0) return `${minutes}m`
    if (minutes === 0) return `${hours}h`
    return `${hours}h ${minutes}m`
}

/** Compact session-time pill for the header — shows "4h 30m" at rest, expands on hover to "Session Remaining: 4h 30m" (slow in, quick out). */
function SessionPill({ minutes }: { minutes: number }) {
    const expired = minutes <= 0
    const label = formatSessionDuration(minutes)
    const colorClass = expired
        ? 'text-red-600 dark:text-red-400'
        : minutes <= 10
        ? 'text-yellow-600 dark:text-yellow-400'
        : 'text-foreground'

    return (
        <div
            className="group flex items-center gap-1.5 overflow-hidden rounded-full bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground"
            title="Session time remaining"
        >
            <ClockIcon className="size-3.5 shrink-0" />
            <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 ease-out sm:group-hover:max-w-[9rem] sm:group-hover:opacity-100 sm:group-hover:duration-500">
                Session Remaining:&nbsp;
            </span>
            <span className={cn('whitespace-nowrap', colorClass)}>{label}</span>
        </div>
    )
}

export function MainLayout() {
    const location = useLocation();
    const pathname = location.pathname;
    const { currentUser, sessionTimeRemaining } = useContext(UserContext);
    const { i18n } = useTranslation();
    const [language, setLanguage] = useState(i18n.language || 'en');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { notifications, unreadCount, loading, markRead, markAllRead, clearAll } = useNotifications();
    const { updateAvailable } = useVersionCheck();
    const [notifOpen, setNotifOpen] = useState(false);
    const pageLabel = usePageBreadcrumb(pathname);

    const notificationGroups = useMemo(() => groupNotificationsByTimeline(notifications), [notifications]);
    const todayCount = useMemo(() => {
        const today = new Date();
        return notifications.filter((n) => {
            const d = new Date(n.created_time);
            return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
        }).length;
    }, [notifications]);


    // Define the sidebar items based on the role
    const renderSidebarItems = () => {
        switch (currentUser?.rolename) {
            case 'Superadmin':
                return (
                    <>
                        <SidebarSection>
                            <SidebarHeading>Main</SidebarHeading>
                            <SidebarItem onClick={() => navigate("/gp/dashboard")} current={pathname.includes('gp/dashboard')}>
                                <HomeIcon />
                                <SidebarLabel>Dashboard</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem onClick={() => navigate("/gp/notifications")} current={pathname.includes('gp/notifications')}>
                                <BellIcon className="size-5 shrink-0 fill-zinc-500 dark:fill-zinc-400" />
                                <SidebarLabel>Notifications</SidebarLabel>
                                {unreadCount > 0 && (
                                    <Badge color="sky" className="ml-auto">{unreadCount > 99 ? '99+' : unreadCount}</Badge>
                                )}
                            </SidebarItem>
                        </SidebarSection>

                        <SidebarSection>
                            <SidebarHeading>Records</SidebarHeading>
                            <SidebarItem onClick={() => navigate("/gp/users")} current={pathname.includes('/gp/users')}>
                                <UserGroupIcon />
                                <SidebarLabel>Users</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem onClick={() => navigate("/gp/auditlog")} current={pathname.includes('gp/auditlog')}>
                                <ShieldCheckIcon />
                                <SidebarLabel>Audit Log</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem onClick={() => navigate("/gp/warehouses")} current={pathname.includes('gp/warehouses')}>
                                <BuildingStorefrontIcon />
                                <SidebarLabel>Warehouses</SidebarLabel>
                            </SidebarItem>
                        </SidebarSection>

                        <SidebarSection>
                            <SidebarHeading>Operations</SidebarHeading>
                            <TrackingSidebarItem />
                            <StockSidebarItem />
                            <SidebarItem onClick={() => navigate("/gp/stockbalancetransfer")} current={pathname.includes('gp/stockbalancetransfer')}>
                                <ArrowsRightLeftIcon />
                                <SidebarLabel>Stock Balance Transfer</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem onClick={() => navigate("/gp/salesorders")} current={pathname.includes('gp/salesorders')}>
                                <ArrowsUpDownIcon />
                                <SidebarLabel>Sales Orders</SidebarLabel>
                            </SidebarItem>
                            <InventorySidebarItem />
                        </SidebarSection>

                        <SidebarSection>
                            <SidebarHeading>Tools</SidebarHeading>
                            <SetupSidebarItem />
                            <ReportSidebarItem />
                            <AppSidebarItem />
                            <SidebarItem onClick={() => navigate("/gp/ai-report-helper")} current={pathname.includes('gp/ai-report-helper')}>
                                <SparklesIcon />
                                <SidebarLabel>AI Report Helper</SidebarLabel>
                                <Badge color="violet" className="ml-auto">Beta</Badge>
                            </SidebarItem>
                            <SidebarItem onClick={() => navigate("/gp/server-health")} current={pathname.includes('gp/server-health')}>
                                <SignalIcon />
                                <SidebarLabel>Server Health</SidebarLabel>
                            </SidebarItem>
                        </SidebarSection>
                    </>
                );
            case 'Staff':
                return (
                    <>
                        <SidebarSection>
                            <SidebarHeading>Main</SidebarHeading>
                            <SidebarItem onClick={() => navigate("/gp/dashboard")} current={pathname.includes('gp/dashboard')}>
                                <HomeIcon />
                                <SidebarLabel>Dashboard</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem onClick={() => navigate("/gp/notifications")} current={pathname.includes('gp/notifications')}>
                                <BellIcon className="size-5 shrink-0 fill-zinc-500 dark:fill-zinc-400" />
                                <SidebarLabel>Notifications</SidebarLabel>
                                {unreadCount > 0 && (
                                    <Badge color="sky" className="ml-auto">{unreadCount > 99 ? '99+' : unreadCount}</Badge>
                                )}
                            </SidebarItem>
                        </SidebarSection>

                        <SidebarSection>
                            <SidebarHeading>Operations</SidebarHeading>
                            <TrackingSidebarItem />
                            <StockSidebarItem />
                            <SidebarItem onClick={() => navigate("/gp/app/salesorders")} current={pathname.includes('gp/app/salesorders')}>
                                <ArrowsUpDownIcon />
                                <SidebarLabel>Sales Orders</SidebarLabel>
                            </SidebarItem>
                            <InventorySidebarItem />
                        </SidebarSection>

                        <SidebarSection>
                            <SidebarHeading>Tools</SidebarHeading>
                            <SetupSidebarItem />
                            <ReportSidebarItem />
                        </SidebarSection>
                    </>
                );
            case 'Supplier':
                return (
                    <SidebarSection>
                        <SidebarHeading>Main</SidebarHeading>
                        <SidebarItem onClick={() => navigate("/gp/dashboard")} current={pathname.includes('gp/dashboard')}>
                            <HomeIcon />
                            <SidebarLabel>Dashboard</SidebarLabel>
                        </SidebarItem>
                        <SidebarItem onClick={() => navigate("/gp/notifications")} current={pathname.includes('gp/notifications')}>
                            <BellIcon className="size-5 shrink-0 fill-zinc-500 dark:fill-zinc-400" />
                            <SidebarLabel>Notifications</SidebarLabel>
                            {unreadCount > 0 && (
                                <Badge color="sky" className="ml-auto">{unreadCount > 99 ? '99+' : unreadCount}</Badge>
                            )}
                        </SidebarItem>
                        <ReportSidebarItem />
                    </SidebarSection>
                );
            default:
                return null; // Or render a message, or redirect
        }
    };

    return (
        <SidebarLayout
            topBanner={
                updateAvailable && (
                    <div className="flex h-10 w-full items-center justify-center gap-3 bg-primary px-4 text-sm text-primary-foreground shadow-sm">
                        <span>A new version of the app is available.</span>
                        <button
                            onClick={() => window.location.reload()}
                            className="rounded-md bg-primary-foreground/15 px-3 py-1 text-xs font-semibold hover:bg-primary-foreground/25"
                        >
                            Refresh
                        </button>
                    </div>
                )
            }
            navbar={
                <Navbar>
                    <NavbarSpacer />
                </Navbar>
            }
            header={
                <>
                    <div className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
                        Workspace <span className="mx-1.5">/</span>
                        <span className="font-semibold text-foreground">{pageLabel}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                        {currentUser && <SessionPill minutes={sessionTimeRemaining} />}
                        <ThemeToggle />
                        {currentUser && (
                            <Dropdown open={notifOpen} onOpenChange={setNotifOpen}>
                                <DropdownButton plain className="relative rounded-full bg-card p-2">
                                    <BellIcon className="size-5 text-muted-foreground" />
                                    {unreadCount > 0 && (
                                        <span className="absolute top-1 right-1 size-1.5 rounded-full bg-destructive ring-2 ring-card" />
                                    )}
                                </DropdownButton>
                                <DropdownMenu
                                    className="flex w-96 max-h-[calc(100vh-6rem)] flex-col overflow-hidden p-0"
                                    anchor="bottom end"
                                >
                                    <div className="flex shrink-0 items-start justify-between gap-3 border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-zinc-900 dark:text-white">Notifications</div>
                                            <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                                                {todayCount > 0
                                                    ? `You have ${todayCount} notification${todayCount !== 1 ? 's' : ''} today.`
                                                    : unreadCount > 0
                                                    ? `You have ${unreadCount > 99 ? '99+' : unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}.`
                                                    : "You're all caught up."}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setNotifOpen(false)}
                                            className="shrink-0 rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                                        >
                                            <XMarkIcon className="size-4" />
                                            <span className="sr-only">Close</span>
                                        </button>
                                    </div>

                                    <div className="min-h-0 flex-1 overflow-y-auto">
                                        {loading ? (
                                            <div className="px-4 py-6 text-center text-sm text-zinc-500">Loading...</div>
                                        ) : notifications.length === 0 ? (
                                            <div className="px-4 py-6 text-center text-sm text-zinc-500">No notifications</div>
                                        ) : (
                                            notificationGroups.map((group) => (
                                                <div key={group.label}>
                                                    <div className="sticky top-0 bg-white/95 px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 backdrop-blur dark:bg-zinc-800/95">
                                                        {group.label}
                                                    </div>
                                                    {group.items.map((n) => {
                                                        const cat = findNotificationCategory(n.type);
                                                        const Icon = cat?.icon ?? DEFAULT_NOTIFICATION_ICON;
                                                        return (
                                                            <button
                                                                key={n.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    if (!n.read_at) markRead(n.id);
                                                                    setNotifOpen(false);
                                                                    navigate(`/gp/notifications/view/${n.id}`);
                                                                }}
                                                                className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/60"
                                                            >
                                                                <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-full', cat?.iconClass ?? 'bg-zinc-500/15 text-zinc-500')}>
                                                                    <Icon className="size-4" />
                                                                </span>
                                                                <span className="min-w-0 flex-1">
                                                                    <span className="flex items-start justify-between gap-2">
                                                                        <span className="flex min-w-0 items-center gap-1.5">
                                                                            {!n.read_at && <span className="size-1.5 shrink-0 rounded-full bg-sky-400" aria-label="Unread" />}
                                                                            <span className="truncate text-sm font-medium text-zinc-900 dark:text-white">{n.title}</span>
                                                                        </span>
                                                                        <span className="shrink-0 text-[11px] text-zinc-400" title={new Date(n.created_time).toLocaleString()}>
                                                                            {formatNotificationRelativeTime(n.created_time)}
                                                                        </span>
                                                                    </span>
                                                                    {n.message && (
                                                                        <span className="mt-0.5 block truncate text-xs text-zinc-500 dark:text-zinc-400">
                                                                            {n.message}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <div className="flex shrink-0 items-center justify-between gap-3 border-t border-zinc-200 px-4 py-2.5 dark:border-zinc-700">
                                        <div className="flex items-center gap-3">
                                            {unreadCount > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => markAllRead()}
                                                    className="text-xs font-medium text-primary hover:text-primary/80"
                                                >
                                                    Mark all read
                                                </button>
                                            )}
                                            {notifications.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => clearAll()}
                                                    className="text-xs font-medium text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
                                                >
                                                    Clear all
                                                </button>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setNotifOpen(false); navigate('/gp/notifications'); }}
                                            className="text-xs font-medium text-primary hover:text-primary/80"
                                        >
                                            View all
                                        </button>
                                    </div>
                                </DropdownMenu>
                            </Dropdown>
                        )}
                        <Dropdown>
                            <DropdownButton plain className="rounded-full p-0">
                                <Avatar square initials={currentUser ? currentUser.fullname[0] : "GP"} className="size-7 bg-brand-teal/10 text-brand-teal" />
                            </DropdownButton>
                            <AccountDropdownMenu anchor="bottom end" />
                        </Dropdown>
                    </div>
                </>
            }
            sidebar={
                <Sidebar>
                    <SidebarHeader>
                        <div className="flex items-center gap-2 px-1">
                            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white">
                                <img src="/images/GPCLogo.png" alt="GPC" className="size-full rounded-lg object-contain p-0.5" />
                            </div>
                            <span className="text-sm font-semibold text-zinc-950 dark:text-white">GPC WMS</span>
                        </div>
                    </SidebarHeader>

                    <SidebarBody>
                        {renderSidebarItems()}

                        {/* <SidebarSection>

                        </SidebarSection>

                        <SidebarSpacer />

                        <SidebarSection>
                            <SidebarItem href="#">
                                <QuestionMarkCircleIcon />
                                <SidebarLabel>Support</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem href="#">
                                <SparklesIcon />
                                <SidebarLabel>Changelog</SidebarLabel>
                            </SidebarItem>
                        </SidebarSection> */}
                    </SidebarBody>

                    <SidebarFooter className="max-lg:hidden">
                        <Dropdown>
                            <DropdownButton as={SidebarItem}>
                                <span className="flex min-w-0 items-center gap-3">
                                    <Avatar square initials={currentUser ? currentUser.fullname[0] : "GH"} className="bg-brand-teal/10 text-brand-teal size-9" />
                                    <span className="min-w-0">
                                        <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">{currentUser ? currentUser.fullname : "Guest"}</span>
                                        <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                                            {currentUser ? currentUser.rolename || "Undefined Role" : "Undefined Role"}
                                        </span>
                                    </span>
                                </span>
                                <ChevronUpIcon />
                            </DropdownButton>
                            <AccountDropdownMenu anchor="top start" />
                        </Dropdown>
                    </SidebarFooter>
                </Sidebar>
            }
        >
            {/* Main Content */}
            <main className="mx-auto max-w-screen-2xl text-foreground antialiased">
                <Outlet />
            </main>
        </SidebarLayout>
    )
}
